// src/components/settings/RouteGroupSettings.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import {
    collection, query, onSnapshot, addDoc, updateDoc, deleteDoc,
    doc, serverTimestamp, orderBy, where
} from 'firebase/firestore';
import { Plus, Pencil, Trash2, Users, X, Check, AlertTriangle, MapPin } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// COLOUR OPTIONS
// ─────────────────────────────────────────────────────────────────────────────
const GROUP_COLORS = [
    { label: 'Blue',   value: '#3b82f6' },
    { label: 'Green',  value: '#10b981' },
    { label: 'Orange', value: '#f59e0b' },
    { label: 'Red',    value: '#ef4444' },
    { label: 'Purple', value: '#8b5cf6' },
    { label: 'Pink',   value: '#ec4899' },
    { label: 'Teal',   value: '#14b8a6' },
    { label: 'Indigo', value: '#6366f1' },
];

// ─────────────────────────────────────────────────────────────────────────────
// MEMBER CHIP
// ─────────────────────────────────────────────────────────────────────────────
const MemberChip = ({ member, onRemove, readOnly }) => (
    <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full">
        <span>{member.name}</span>
        {member.accountType === 'fieldWorker' && (
            <span className="text-blue-400 text-[10px]">• Field</span>
        )}
        {!readOnly && (
            <button
                onClick={() => onRemove(member.id)}
                className="ml-1 text-blue-400 hover:text-blue-700 transition-colors"
                aria-label={`Remove ${member.name}`}
            >
                <X size={11} />
            </button>
        )}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// GROUP FORM (create / edit inline)
// ─────────────────────────────────────────────────────────────────────────────
const GroupForm = ({ initial, allWorkers, onSave, onCancel, isSaving }) => {
    const [name, setName]       = useState(initial?.name || '');
    const [color, setColor]     = useState(initial?.color || GROUP_COLORS[0].value);
    const [members, setMembers] = useState(initial?.memberIds || []);
    const [defaultStartingAddress, setDefaultStartingAddress] = useState(initial?.defaultStartingAddress || '');
    const [search, setSearch]   = useState('');
    const [error, setError]     = useState('');

    const available = allWorkers.filter(w =>
        !members.includes(w.id) &&
        w.name.toLowerCase().includes(search.toLowerCase())
    );

    const handleAddMember = (worker) => {
        setMembers(prev => [...prev, worker.id]);
        setSearch('');
    };

    const handleRemoveMember = (id) => {
        setMembers(prev => prev.filter(m => m !== id));
    };

    const handleSubmit = () => {
        const trimmed = name.trim();
        if (!trimmed) { setError('Group name is required.'); return; }
        if (members.length === 0) { setError('Add at least one team member.'); return; }
        setError('');
        onSave({
            name: trimmed,
            color,
            memberIds: members,
            defaultStartingAddress: defaultStartingAddress.trim(),
        });
    };

    const selectedWorkers = members
        .map(id => allWorkers.find(w => w.id === id))
        .filter(Boolean);

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
            {/* Name */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
                <input
                    value={name}
                    onChange={e => { setName(e.target.value); setError(''); }}
                    placeholder="e.g. Lawn Crew A"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Color */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Colour</label>
                <div className="flex flex-wrap gap-2">
                    {GROUP_COLORS.map(c => (
                        <button
                            key={c.value}
                            onClick={() => setColor(c.value)}
                            className={`w-7 h-7 rounded-full border-2 transition-all ${
                                color === c.value ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'
                            }`}
                            style={{ backgroundColor: c.value }}
                            title={c.label}
                            aria-label={c.label}
                        />
                    ))}
                </div>
            </div>

            {/* Default Starting Address */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                    <MapPin size={13} className="text-gray-500" />
                    Default Starting Address
                    <span className="ml-1 text-xs font-normal text-gray-400">(optional)</span>
                </label>
                <input
                    value={defaultStartingAddress}
                    onChange={e => setDefaultStartingAddress(e.target.value)}
                    placeholder="e.g. 123 Main St, Thunder Bay, ON"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                    Where this crew typically starts their day (your shop, depot, or yard). Used as the default origin when optimizing routes — overridable per day.
                </p>
            </div>

            {/* Members */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Members
                    <span className="ml-2 text-xs font-normal text-gray-400">
                        Includes employees and field workers
                    </span>
                </label>

                {/* Selected chips */}
                {selectedWorkers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {selectedWorkers.map(w => (
                            <MemberChip key={w.id} member={w} onRemove={handleRemoveMember} />
                        ))}
                    </div>
                )}

                {/* Search & add */}
                <div className="relative">
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search to add members..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {search && available.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {available.map(w => (
                                <button
                                    key={w.id}
                                    onClick={() => handleAddMember(w)}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 text-left transition-colors"
                                >
                                    <span className="flex-1">{w.name}</span>
                                    {w.accountType === 'fieldWorker' && (
                                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Field</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                    {search && available.length === 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow px-3 py-2 text-sm text-gray-400">
                            No matches found
                        </div>
                    )}
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertTriangle size={14} />
                    <span>{error}</span>
                </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                    {isSaving ? (
                        <span>Saving…</span>
                    ) : (
                        <>
                            <Check size={15} />
                            <span>Save Group</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// GROUP CARD (display)
// ─────────────────────────────────────────────────────────────────────────────
const GroupCard = ({ group, allWorkers, onEdit, onDelete }) => {
    const members = (group.memberIds || [])
        .map(id => allWorkers.find(w => w.id === id))
        .filter(Boolean);

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div
                        className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: group.color || '#3b82f6' }}
                    />
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">{group.name}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {members.length} {members.length === 1 ? 'member' : 'members'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        onClick={() => onEdit(group)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        aria-label="Edit group"
                    >
                        <Pencil size={15} />
                    </button>
                    <button
                        onClick={() => onDelete(group)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label="Delete group"
                    >
                        <Trash2 size={15} />
                    </button>
                </div>
            </div>

            {group.defaultStartingAddress && (
                <div className="flex items-start gap-1.5 mt-2 text-xs text-gray-500">
                    <MapPin size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="truncate" title={group.defaultStartingAddress}>
                        Starts from: {group.defaultStartingAddress}
                    </span>
                </div>
            )}

            {members.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                    {members.map(m => (
                        <MemberChip key={m.id} member={m} readOnly />
                    ))}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// DELETE CONFIRMATION
// ─────────────────────────────────────────────────────────────────────────────
const DeleteConfirm = ({ group, onConfirm, onCancel, isDeleting }) => (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle size={16} />
            <span className="text-sm font-semibold">Delete "{group.name}"?</span>
        </div>
        <p className="text-xs text-red-600">
            This removes the group from routing. Jobs already assigned to individuals in this group are not affected.
        </p>
        <div className="flex gap-2">
            <button
                onClick={onCancel}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
                Cancel
            </button>
            <button
                onClick={onConfirm}
                disabled={isDeleting}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
                {isDeleting ? 'Deleting…' : 'Yes, Delete'}
            </button>
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const RouteGroupSettings = ({ currentUserProfile }) => {
    const [groups, setGroups]             = useState([]);
    const [allWorkers, setAllWorkers]     = useState([]);
    const [isLoading, setIsLoading]       = useState(true);
    const [showForm, setShowForm]         = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [deletingGroup, setDeletingGroup] = useState(null);
    const [isSaving, setIsSaving]         = useState(false);
    const [isDeleting, setIsDeleting]     = useState(false);
    const [successMsg, setSuccessMsg]     = useState('');

    const orgId = currentUserProfile?.organizationId;

    // ── Fetch route groups ──────────────────────────────────────────────────
    useEffect(() => {
        if (!orgId) return;
        const q = query(
            collection(db, `organizations/${orgId}/routeGroups`),
            orderBy('name')
        );
        const unsub = onSnapshot(q, snap => {
            setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        }, err => {
            console.error('Error fetching route groups:', err);
            setIsLoading(false);
        });
        return () => unsub();
    }, [orgId]);

    // ── Fetch all workers (users + field workers) org-scoped ───────────────
    useEffect(() => {
        if (!orgId) return;

        // Query regular users scoped to org
        const usersQ = query(
            collection(db, 'users'),
            where('organizationId', '==', orgId)
        );

        // Query field workers from org subcollection
        const fwQ = query(
            collection(db, `organizations/${orgId}/fieldWorkers`),
            where('status', '==', 'active')
        );

        let latestUsers = [];
        let latestFW = [];

        const merge = () => {
            const combined = [
                ...latestUsers
                    .filter(u => u.status !== 'inactive' && u.status !== 'removed')
                    .map(u => ({ ...u, accountType: u.accountType || 'user' })),
                ...latestFW.map(fw => ({ ...fw, accountType: 'fieldWorker' })),
            ].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setAllWorkers(combined);
        };

        const unsubUsers = onSnapshot(usersQ, snap => {
            latestUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            merge();
        }, err => console.error('Error fetching users for route groups:', err));

        const unsubFW = onSnapshot(fwQ, snap => {
            latestFW = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            merge();
        }, err => console.error('Error fetching field workers for route groups:', err));

        return () => { unsubUsers(); unsubFW(); };
    }, [orgId]);

    const showSuccess = (msg) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 3000);
    };

    // ── Create ──────────────────────────────────────────────────────────────
    const handleCreate = async (data) => {
        if (!orgId) return;
        setIsSaving(true);
        try {
            await addDoc(collection(db, `organizations/${orgId}/routeGroups`), {
                ...data,
                createdAt: serverTimestamp(),
                createdBy: currentUserProfile.id,
            });
            setShowForm(false);
            showSuccess('Route group created.');
        } catch (err) {
            console.error('Error creating route group:', err);
        }
        setIsSaving(false);
    };

    // ── Update ──────────────────────────────────────────────────────────────
    const handleUpdate = async (data) => {
        if (!orgId || !editingGroup) return;
        setIsSaving(true);
        try {
            await updateDoc(doc(db, `organizations/${orgId}/routeGroups`, editingGroup.id), {
                ...data,
                updatedAt: serverTimestamp(),
                updatedBy: currentUserProfile.id,
            });
            setEditingGroup(null);
            showSuccess('Route group updated.');
        } catch (err) {
            console.error('Error updating route group:', err);
        }
        setIsSaving(false);
    };

    // ── Delete ──────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!orgId || !deletingGroup) return;
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, `organizations/${orgId}/routeGroups`, deletingGroup.id));
            setDeletingGroup(null);
            showSuccess('Route group deleted.');
        } catch (err) {
            console.error('Error deleting route group:', err);
        }
        setIsDeleting(false);
    };

    if (isLoading) {
        return (
            <div className="p-6 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg font-bold text-gray-900">Route Groups</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Create named groups of workers for one-tap route planning. Members can belong to multiple groups.
                    </p>
                </div>
                {!showForm && !editingGroup && (
                    <button
                        onClick={() => setShowForm(true)}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex-shrink-0"
                    >
                        <Plus size={15} />
                        New Group
                    </button>
                )}
            </div>

            {/* Success message */}
            {successMsg && (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
                    <Check size={14} />
                    {successMsg}
                </div>
            )}

            {/* Create form */}
            {showForm && (
                <GroupForm
                    allWorkers={allWorkers}
                    onSave={handleCreate}
                    onCancel={() => setShowForm(false)}
                    isSaving={isSaving}
                />
            )}

            {/* Empty state */}
            {groups.length === 0 && !showForm && (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <Users size={22} className="text-blue-500" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">No route groups yet</h3>
                    <p className="text-xs text-gray-400 max-w-xs mx-auto">
                        Create a group like "Lawn Crew A" to select an entire crew with one tap when planning routes.
                    </p>
                </div>
            )}

            {/* Group list */}
            <div className="space-y-3">
                {groups.map(group => {
                    // Show delete confirm inline
                    if (deletingGroup?.id === group.id) {
                        return (
                            <DeleteConfirm
                                key={group.id}
                                group={group}
                                onConfirm={handleDelete}
                                onCancel={() => setDeletingGroup(null)}
                                isDeleting={isDeleting}
                            />
                        );
                    }
                    // Show edit form inline
                    if (editingGroup?.id === group.id) {
                        return (
                            <GroupForm
                                key={group.id}
                                initial={group}
                                allWorkers={allWorkers}
                                onSave={handleUpdate}
                                onCancel={() => setEditingGroup(null)}
                                isSaving={isSaving}
                            />
                        );
                    }
                    return (
                        <GroupCard
                            key={group.id}
                            group={group}
                            allWorkers={allWorkers}
                            onEdit={g => { setEditingGroup(g); setShowForm(false); }}
                            onDelete={g => setDeletingGroup(g)}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default RouteGroupSettings;